/**
 * Comprehensive Fallback Manager for Token Refresh Failures
 * Implements multi-layer fallback strategies with graceful degradation
 */

import { AppDispatch } from '../redux/store';
import { logoutUser } from '../redux/slices/userSlice';

// Fallback Strategy Types
export type FallbackStrategy = 'retry' | 'recover' | 'degrade' | 'redirect' | 'notify';

export interface FallbackAction {
  strategy: FallbackStrategy;
  priority: number;
  condition?: (error: any, context: FallbackContext) => boolean;
  execute: (context: FallbackContext) => Promise<FallbackResult>;
}

export interface FallbackContext {
  error: any;
  operation: string;
  operationType: OperationType;
  attempt: number;
  maxAttempts: number;
  dispatch: AppDispatch;
  metadata?: Record<string, any>;
}

export interface FallbackResult {
  success: boolean;
  action: string;
  shouldRetry: boolean;
  userMessage?: string;
  nextStrategy?: FallbackStrategy;
}

// Operation Types for different fallback strategies
export type OperationType =
  | 'chat-message'
  | 'admin-api'
  | 'conversation-load'
  | 'user-profile'
  | 'file-upload'
  | 'critical-operation';

// Error Classification for intelligent fallback decisions
export enum ErrorClass {
  NETWORK_ERROR = 'network',
  TOKEN_EXPIRED = 'expired',
  TOKEN_INVALID = 'invalid',
  SERVICE_UNAVAILABLE = 'service',
  USER_REVOKED = 'revoked',
  PERMISSION_DENIED = 'permission',
  UNKNOWN = 'unknown',
}

/**
 * Classifies errors for appropriate fallback strategy selection
 */
export const classifyError = (error: any): ErrorClass => {
  if (!error) return ErrorClass.UNKNOWN;

  const message = error.message?.toLowerCase() || '';
  const code = error.code?.toLowerCase() || '';

  // Network-related errors
  if (
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('connection') ||
    code.includes('network')
  ) {
    return ErrorClass.NETWORK_ERROR;
  }

  // Token expiration
  if (
    message.includes('expired') ||
    (message.includes('token') && message.includes('expired')) ||
    code === 'auth/id-token-expired' ||
    code === 'auth/token-expired'
  ) {
    return ErrorClass.TOKEN_EXPIRED;
  }

  // Invalid token
  if (
    message.includes('invalid') ||
    message.includes('unauthorized') ||
    code === 'auth/invalid-user-token' ||
    code === 'auth/user-token-expired'
  ) {
    return ErrorClass.TOKEN_INVALID;
  }

  // Service unavailable
  if (
    message.includes('service') ||
    message.includes('unavailable') ||
    code.includes('unavailable') ||
    error.status === 503
  ) {
    return ErrorClass.SERVICE_UNAVAILABLE;
  }

  // User access revoked
  if (
    message.includes('revoked') ||
    message.includes('disabled') ||
    code === 'auth/user-disabled' ||
    code === 'auth/user-not-found'
  ) {
    return ErrorClass.USER_REVOKED;
  }

  // Permission denied
  if (
    message.includes('permission') ||
    message.includes('forbidden') ||
    error.status === 403 ||
    code === 'auth/insufficient-permission'
  ) {
    return ErrorClass.PERMISSION_DENIED;
  }

  return ErrorClass.UNKNOWN;
};

/**
 * Retry Strategy with Exponential Backoff
 */
export const createRetryAction = (maxAttempts = 3): FallbackAction => ({
  strategy: 'retry',
  priority: 1,
  condition: (error, context) => {
    const errorClass = classifyError(error);
    return (
      context.attempt < maxAttempts &&
      (errorClass === ErrorClass.NETWORK_ERROR || errorClass === ErrorClass.SERVICE_UNAVAILABLE)
    );
  },
  execute: async (context): Promise<FallbackResult> => {
    const delay = Math.min(1000 * Math.pow(2, context.attempt), 10000); // Max 10s delay

    console.log(
      `[FallbackManager] Retrying ${context.operation} (attempt ${
        context.attempt + 1
      }/${maxAttempts}) after ${delay}ms`
    );

    await new Promise(resolve => setTimeout(resolve, delay));

    return {
      success: true,
      action: `retry_attempt_${context.attempt + 1}`,
      shouldRetry: true,
      userMessage: context.attempt === 0 ? 'Retrying...' : undefined,
    };
  },
});

/**
 * Recovery Strategy - Attempt session recovery
 */
export const createRecoveryAction = (): FallbackAction => ({
  strategy: 'recover',
  priority: 2,
  condition: (error, context) => {
    const errorClass = classifyError(error);
    return errorClass === ErrorClass.TOKEN_EXPIRED || errorClass === ErrorClass.TOKEN_INVALID;
  },
  execute: async (context): Promise<FallbackResult> => {
    console.log(`[FallbackManager] Attempting session recovery for ${context.operation}`);

    try {
      // Import dynamically to avoid circular dependencies
      const { refreshToken } = await import('../redux/slices/userSlice');
      const refreshResult = await context.dispatch(refreshToken(true));

      if (refreshResult.meta.requestStatus === 'fulfilled') {
        return {
          success: true,
          action: 'session_recovered',
          shouldRetry: true,
          userMessage: 'Session refreshed, retrying...',
        };
      }
    } catch (recoveryError) {
      console.error('[FallbackManager] Session recovery failed:', recoveryError);
    }

    return {
      success: false,
      action: 'recovery_failed',
      shouldRetry: false,
      nextStrategy: 'degrade',
    };
  },
});

/**
 * Degradation Strategy - Graceful feature degradation
 */
export const createDegradationAction = (): FallbackAction => ({
  strategy: 'degrade',
  priority: 3,
  condition: () => true, // Always applicable as last resort
  execute: async (context): Promise<FallbackResult> => {
    console.log(`[FallbackManager] Applying graceful degradation for ${context.operation}`);

    const degradationStrategies: Record<OperationType, () => FallbackResult> = {
      'chat-message': () => ({
        success: true,
        action: 'degrade_to_offline_draft',
        shouldRetry: false,
        userMessage: 'Message saved as draft. Please sign in to send.',
      }),

      'admin-api': () => ({
        success: true,
        action: 'degrade_to_readonly',
        shouldRetry: false,
        userMessage: 'Viewing in read-only mode. Please sign in for full access.',
      }),

      'conversation-load': () => ({
        success: true,
        action: 'degrade_to_cached_data',
        shouldRetry: false,
        userMessage: 'Showing cached conversations. Please refresh when connected.',
      }),

      'user-profile': () => ({
        success: true,
        action: 'degrade_to_basic_profile',
        shouldRetry: false,
        userMessage: 'Limited profile access. Please sign in again.',
      }),

      'file-upload': () => ({
        success: false,
        action: 'degrade_block_upload',
        shouldRetry: false,
        userMessage: 'File upload unavailable. Please sign in and try again.',
      }),

      'critical-operation': () => ({
        success: false,
        action: 'degrade_block_operation',
        shouldRetry: false,
        userMessage: 'This operation requires authentication. Please sign in.',
      }),
    };

    const strategy = degradationStrategies[context.operationType];
    return strategy
      ? strategy()
      : {
          success: false,
          action: 'degrade_generic',
          shouldRetry: false,
          userMessage: 'Authentication required. Please sign in to continue.',
        };
  },
});

/**
 * Redirect Strategy - Guide user to re-authentication
 */
export const createRedirectAction = (): FallbackAction => ({
  strategy: 'redirect',
  priority: 4,
  condition: error => {
    const errorClass = classifyError(error);
    return errorClass === ErrorClass.USER_REVOKED || errorClass === ErrorClass.PERMISSION_DENIED;
  },
  execute: async (context): Promise<FallbackResult> => {
    console.log(`[FallbackManager] Redirecting to sign-in for ${context.operation}`);

    // Clear invalid session
    await context.dispatch(logoutUser());

    // Redirect to sign-in (this would be handled by the app's routing)
    return {
      success: true,
      action: 'redirect_to_signin',
      shouldRetry: false,
      userMessage: 'Please sign in to continue.',
    };
  },
});

/**
 * Fallback Configuration for different operation types
 */
export const FALLBACK_CONFIGS: Record<OperationType, FallbackAction[]> = {
  'chat-message': [createRetryAction(2), createRecoveryAction(), createDegradationAction()],

  'admin-api': [
    createRetryAction(3),
    createRecoveryAction(),
    createRedirectAction(),
    createDegradationAction(),
  ],

  'conversation-load': [createRetryAction(2), createRecoveryAction(), createDegradationAction()],

  'user-profile': [createRecoveryAction(), createRedirectAction(), createDegradationAction()],

  'file-upload': [createRetryAction(1), createRecoveryAction(), createDegradationAction()],

  'critical-operation': [createRetryAction(1), createRecoveryAction(), createRedirectAction()],
};

/**
 * Main Fallback Manager - Orchestrates fallback strategies
 */
export class FallbackManager {
  private static instance: FallbackManager;
  private activeOperations = new Map<string, FallbackContext>();

  static getInstance(): FallbackManager {
    if (!FallbackManager.instance) {
      FallbackManager.instance = new FallbackManager();
    }
    return FallbackManager.instance;
  }

  /**
   * Execute fallback strategy for a failed operation
   */
  async executeFallback(
    error: any,
    operation: string,
    operationType: OperationType,
    dispatch: AppDispatch,
    metadata?: Record<string, any>
  ): Promise<FallbackResult> {
    const operationId = `${operationType}-${operation}-${Date.now()}`;

    const context: FallbackContext = {
      error,
      operation,
      operationType,
      attempt: 0,
      maxAttempts: 3,
      dispatch,
      metadata,
    };

    this.activeOperations.set(operationId, context);

    try {
      const actions = FALLBACK_CONFIGS[operationType] || [];

      for (const action of actions.sort((a, b) => a.priority - b.priority)) {
        if (!action.condition || action.condition(error, context)) {
          console.log(`[FallbackManager] Executing ${action.strategy} for ${operation}`);

          const result = await action.execute(context);

          if (result.success || !result.shouldRetry) {
            this.activeOperations.delete(operationId);
            return result;
          }

          // If strategy suggests next strategy, continue with that
          if (result.nextStrategy) {
            const nextAction = actions.find(a => a.strategy === result.nextStrategy);
            if (nextAction) {
              continue;
            }
          }
        }
      }

      // If no strategy worked, return generic failure
      return {
        success: false,
        action: 'all_strategies_failed',
        shouldRetry: false,
        userMessage: 'Unable to complete operation. Please try again later.',
      };
    } finally {
      this.activeOperations.delete(operationId);
    }
  }

  /**
   * Check if an operation is currently being handled
   */
  isOperationActive(operation: string, operationType: OperationType): boolean {
    const contexts = Array.from(this.activeOperations.values());
    for (const context of contexts) {
      if (context.operation === operation && context.operationType === operationType) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get statistics about fallback operations
   */
  getStats(): { activeOperations: number; operationTypes: Record<OperationType, number> } {
    const operationTypes: Record<OperationType, number> = {
      'chat-message': 0,
      'admin-api': 0,
      'conversation-load': 0,
      'user-profile': 0,
      'file-upload': 0,
      'critical-operation': 0,
    };

    const contexts = Array.from(this.activeOperations.values());
    for (const context of contexts) {
      operationTypes[context.operationType] = (operationTypes[context.operationType] || 0) + 1;
    }

    return {
      activeOperations: this.activeOperations.size,
      operationTypes,
    };
  }
}

/**
 * Convenience function for executing fallback strategies
 */
export const executeFallback = async (
  error: any,
  operation: string,
  operationType: OperationType,
  dispatch: AppDispatch,
  metadata?: Record<string, any>
): Promise<FallbackResult> => {
  const manager = FallbackManager.getInstance();
  return manager.executeFallback(error, operation, operationType, dispatch, metadata);
};
